/**
 * Admin thunks — `content-ingestion-pipeline` Sub-R5.1
 * (save-to-server for on-page admin edits).
 *
 * The `saveAdminEditThunk` bridges the DailyPage edit drawer's
 * "Save" button to the admin gateway's `PostContentV1` operation.
 * On success it invalidates the day cache by re-dispatching
 * `fetchContent({date})` so the SPA re-hydrates from the server
 * (matching the "single source of truth = the target table" rule).
 * On failure it flips the per-record `saveById[id].status` to
 * `'failed'` with the error message so the drawer can render an
 * inline error banner.
 *
 * Function-length budget: every export stays under 20 lines per
 * Requirement 3.7. Non-blank/non-comment lines only.
 */

import type { AppDispatch, RootState } from '../index';
import { postContent, type PostContentInput } from '../../lib/api/pending-api-client';
import {
  adminSaveFailed,
  adminSaveRequested,
  adminSaveSucceeded,
} from '../slices/admin-slice';
import { fetchContent } from './content-thunks';

/** Inputs accepted by {@link saveAdminEditThunk}. */
export interface SaveAdminEditInput extends PostContentInput {
  /**
   * Date to re-fetch after a successful save. Envelope-table saves
   * pass the same value they used as the target-record PK (`date`
   * for news + releases; `MM-DD` for facts — but for facts the
   * refetch date must be the full `YYYY-MM-DD` display date).
   * The caller supplies the SPA-side "which day is this record
   * shown on" here.
   */
  readonly refetchDate: string;
}

function requireIdToken(getState: () => RootState): string {
  const idToken = getState().auth.idToken;
  if (idToken === undefined || idToken === '') {
    throw new Error('not-authenticated');
  }
  return idToken;
}

/**
 * Save an admin edit to the target-table record and refetch the day
 * cache so the render reflects the server state
 * (`content-ingestion-pipeline` Sub-R5.1).
 *
 * @param input Post-content input + the display date to refetch.
 */
export function saveAdminEditThunk(input: SaveAdminEditInput) {
  return async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    const idToken = requireIdToken(getState);
    dispatch(adminSaveRequested({ id: input.uuid }));
    try {
      await postContent(
        {
          tableName: input.tableName,
          uuid: input.uuid,
          date: input.date,
          patch: input.patch,
          expectedVersion: input.expectedVersion,
        },
        idToken,
      );
      dispatch(adminSaveSucceeded({ id: input.uuid }));
      await dispatch(fetchContent({ date: input.refetchDate }));
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      dispatch(adminSaveFailed({ id: input.uuid, error: message }));
    }
  };
}
